import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellOff, Volume2 } from "lucide-react";
import { api, formatMoney } from "../lib/api";
import { useI18n, renderNotif } from "../lib/i18n";
import { isMuted, setMuted, playGeneralChime, playOrderChime } from "../lib/sound";
import { pollMs, useDataSaver } from "../lib/dataSaver";

export default function NotificationBell() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [muted, setMutedState] = useState(isMuted());
  const latestRef = useRef(null);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/notify/notifications");
      setItems(data.items);
      setUnread(data.unread);
      const newest = data.items[0];
      if (newest && latestRef.current && newest.id !== latestRef.current) {
        const fresh = data.items.filter((n) => !n.read_at);
        const hasOrder = fresh.some((n) => n.type === "order.new" || n.type === "payment.request");
        if (hasOrder) playOrderChime();
        else if (fresh.length) playGeneralChime();
      }
      if (newest) latestRef.current = newest.id;
    } catch (e) {
      console.debug("notification poll failed", e?.message);
    }
  }, []);

  const dataSaver = useDataSaver();
  useEffect(() => {
    poll();
    const id = setInterval(poll, pollMs(8000));
    return () => clearInterval(id);
  }, [poll, dataSaver]);

  const openDropdown = async () => {
    setOpen(!open);
    if (!open && unread > 0) {
      await api.post("/notify/notifications/read", { all: true });
      setUnread(0);
    }
  };

  const toggleMute = () => {
    setMuted(!muted);
    setMutedState(!muted);
  };

  const go = (n) => {
    setOpen(false);
    if (n.link_path) navigate(n.link_path);
  };

  return (
    <div className="relative">
      <button className="relative p-2 rounded-xl hover:bg-rail-soft transition-colors" onClick={openDropdown} data-testid="notification-bell">
        <Bell size={20} className="text-rail-ink/85" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-pink text-[10px] font-bold flex items-center justify-center px-1" data-testid="notification-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-12 z-50 w-80 rounded-2xl bg-surface text-ink border border-line shadow-card p-2 max-h-[70vh] overflow-y-auto" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-3 py-2">
            <p className="font-semibold text-sm">{t("shell.notifications")}</p>
            <button className="text-ink/65 hover:text-brand-ink" onClick={toggleMute} title={muted ? t("notify.unmute") : t("notify.mute")} data-testid="mute-toggle">
              {muted ? <BellOff size={16} /> : <Volume2 size={16} />}
            </button>
          </div>
          {items.length === 0 ? (
            <p className="text-sm text-ink/65 px-3 py-4">{t("shell.noNotifications")}</p>
          ) : (
            items.map((n) => {
              const { title, body } = renderNotif(n, t, formatMoney);
              return (
                <button key={n.id} className="w-full text-left rounded-xl px-3 py-2.5 hover:bg-inset" onClick={() => go(n)} data-testid={`notification-item-${n.id}`}>
                  <p className="text-sm font-semibold">{title}</p>
                  <p className="text-xs text-ink/70">{body}</p>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
